// @flow
import {merge, padStart} from 'lodash'
import uuid from 'uuid/v4'
import config from 'config'
import {encryptAES, decryptAES} from 'modules/Common/utils/index'
import {
  buildDigestHeader,
  parseDigestHeader
} from 'modules/Authenticate/utils/index'

import type {LoginParams} from 'modules/Authenticate/types/index'

export const getDeviceIdentifier = (): string => {
  let deviceUniqueIdentifier = localStorage.getItem('deviceUniqueIdentifier')
  if (!deviceUniqueIdentifier) {
    deviceUniqueIdentifier = uuid()
    localStorage.setItem('deviceUniqueIdentifier', deviceUniqueIdentifier)
  }
  return deviceUniqueIdentifier
}

export const request = (
  path: string,
  options: Object = {},
  isNotRequireEncrypt?: boolean = false,
  shouldNotDecryptResponse?: boolean = false
): Promise<*> => {
  let token = JSON.parse(localStorage.getItem('session') || '{}').accessToken

  return new Promise((resolve, reject) => {
    let optionsEcrypted = {...options}
    if (!isNotRequireEncrypt && config.ENCRYPT_DATA) {
      if (optionsEcrypted.body) {
        optionsEcrypted.body = encryptAES(optionsEcrypted.body)
      }
    }
    fetch(
      `${config.API_URL}/${config.API_VERSION}/${path}`,
      merge(
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept:
              shouldNotDecryptResponse ||
              isNotRequireEncrypt ||
              !config.ENCRYPT_DATA
                ? 'application/json'
                : 'text/plain',
            'Content-Type': 'application/json'
          }
        },
        optionsEcrypted
      )
    )
      .then(response => {
        if (response.status === 200) {
          return response.text()
        } else {
          reject(response.status)
        }
      })
      .then((res: any) => {
        let response = null
        if (
          shouldNotDecryptResponse ||
          isNotRequireEncrypt ||
          !config.ENCRYPT_DATA
        ) {
          response = JSON.parse(res || '{}')
        } else {
          try {
            response = decryptAES(res)
          } catch (err) {
            throw Error('cannot decrypt data')
          }
        }

        if (response && response.success) {
          resolve(response)
        } else {
          reject(
            response
              ? {
                code: response.code,
                message: response.message,
                details: response.details
              }
              : null
          )
        }
      })
      .catch(error => reject(error))
  })
}

export const matrixRequest = (
  path: string,
  options: Object = {}
): Promise<*> => {
  return new Promise((resolve, reject) => {
    fetch(
      `${config.API_URL}/${path}`,
      merge(
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'opaque'
        },
        options
      )
    )
      .then(response => {
        if (response.status === 200) {
          return response.json()
        } else {
          reject(response.status)
        }
      })
      .then(response => {
        if (response) {
          resolve(response)
        } else {
          reject(null)
        }
      })
      .catch(error => reject(error))
  })
}

export const digestRequest = async (
  path: string,
  options: Object = {},
  params: LoginParams,
  isFirstTime: boolean
): Promise<*> => {
  let finalParams = {
    ...params,
    deviceUniqueIdentifier: getDeviceIdentifier()
  }

  if (!isFirstTime) {
    const md5 = (await import('blueimp-md5')).default
    const nc = localStorage.getItem('nc') || '00000001'
    let nextNc = parseInt(nc, 10) + 1
    if (nextNc > 99999999) {
      nextNc = 1
    }
    const nextNcString = padStart(nextNc.toString(), 8, '0')
    localStorage.setItem('nc', nextNcString)
    const uri = `/${config.API_VERSION}/${path}`
    const cnonce = uuid().slice(0, 8)

    const HA1 = md5(
      `${finalParams.username}:${finalParams.realm}:${finalParams.password}`
    )
    const HA2 = md5(`${options.method}:${uri}:${md5(options.body)}`)
    const response = md5(
      `${HA1}:${finalParams.nonce}:${nextNcString}:${cnonce}:${finalParams.qop}:${HA2}`
    )

    finalParams = {
      ...finalParams,
      nc: nextNcString,
      cnonce: cnonce,
      uri: uri,
      algorithm: 'md5',
      response: response,
      password: undefined
    }
  }

  return new Promise((resolve, reject) => {
    fetch(
      `${config.API_URL}/${config.API_VERSION}/${path}`,
      merge(
        {
          headers: {
            Authorization: buildDigestHeader(finalParams),
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        },
        options
      )
    )
      .then(response => {
        if (isFirstTime) {
          return Promise.resolve(
            parseDigestHeader(response.headers.get('www-authenticate'))
          )
        } else {
          if (response.status === 200) {
            return response.json()
          } else {
            reject(response.status)
          }
        }
      })
      .then(response => {
        if (isFirstTime) {
          resolve(response)
        } else {
          if (response && response.success) {
            resolve(response)
          } else {
            reject(response)
          }
        }
      })
      .catch(error => {
        reject(error)
      })
  })
}
